import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ROLES = [
  {
    n: '01',
    id: 'artist',
    label: 'Artiste',
    sub: 'DJ · producteur · performer',
    steps: [
      {
        title: 'Cree ton compte',
        desc: 'Inscris-toi en choisissant le role "Artiste". Un email de verification te sera envoye.',
      },
      {
        title: 'Complete ton profil',
        desc: 'Nom d\'artiste, bio, genres musicaux, localisation et photo. Ton profil sera visible publiquement sur la plateforme.',
      },
      {
        title: 'Gere tes disponibilites',
        desc: 'Marque tes jours disponibles ou bloques sur le calendrier. Les promoteurs verront tes dispos sur ton profil public.',
      },
      {
        title: 'Recois des bookings',
        desc: 'Les promoteurs t\'envoient des invitations pour leurs evenements. Tu acceptes ou refuses depuis ton dashboard.',
        note: 'Booking sur invitation uniquement',
        explain:
          'Ce sont les promoteurs qui viennent vers toi. Chaque proposition correspond a un evenement concret et un vrai besoin. Ca maintient un equilibre sain sur la plateforme : ton profil et tes disponibilites parlent pour toi, pas de demarchage.',
      },
      {
        title: 'Echange et finalise',
        desc: 'Discute les details via la messagerie integree, partage des fichiers (riders, contrats) et recois ta feuille de route PDF.',
      },
    ],
  },
  {
    n: '02',
    id: 'promoter',
    label: 'Promoteur',
    sub: 'Organisateur d\'evenements',
    steps: [
      {
        title: 'Cree ton compte et ton profil',
        desc: 'Inscris-toi en "Promoteur", puis renseigne le nom de ton collectif ou structure, ta ville et une courte bio.',
      },
      {
        title: 'Cree un evenement',
        desc: 'Titre, date, lieu, description. Tu peux sauvegarder en brouillon ou publier directement.',
      },
      {
        title: 'Trouve des artistes',
        desc: 'Parcours le catalogue par genre, ville, pays ou disponibilite. Consulte les profils publics et leurs calendriers.',
      },
      {
        title: 'Envoie une invitation de booking',
        desc: 'Selectionne un de tes evenements, propose un cachet et un message personnalise. L\'artiste recoit une notification en temps reel et un email.',
        note: 'C\'est toi qui fais le premier pas',
        explain:
          'L\'initiative vient toujours du promoteur. Tu choisis l\'artiste qui correspond a ton evenement, et l\'artiste decide librement d\'accepter ou non. Un echange equilibre ou chacun a son role.',
      },
      {
        title: 'Suis et gere',
        desc: 'Suis le statut de tes bookings (en attente, accepte, refuse), echange par messagerie, et genere la feuille de route.',
      },
    ],
  },
  {
    n: '03',
    id: 'booker',
    label: 'Booker / Agent',
    sub: 'Agent d\'artistes · label · agence',
    important:
      'Chaque artiste doit d\'abord avoir son propre compte Striderr. Tu ne peux pas creer de compte pour eux. Une fois inscrits, tu les invites dans ton roster.',
    steps: [
      {
        title: 'Tes artistes s\'inscrivent individuellement',
        desc: 'Chaque artiste de ton roster cree son propre compte "Artiste" et complete son profil (nom, genres, photo, dispos).',
        note: 'Prerequis obligatoire',
        explain:
          'Chaque artiste garde le controle total de son compte et de ses donnees. C\'est aussi ce qui leur permet de recevoir des bookings directement s\'ils le souhaitent, meme en faisant partie de ton roster.',
      },
      {
        title: 'Cree ton compte booker',
        desc: 'Inscris-toi en "Bookeur" et renseigne ton profil d\'agence : nom, bio, ville, telephone, site web.',
      },
      {
        title: 'Invite tes artistes dans ton roster',
        desc: 'Depuis ton dashboard, envoie une invitation a chaque artiste. Ils recevront une notification et pourront accepter ou refuser.',
        note: 'L\'artiste peut aussi t\'inviter depuis son cote',
      },
      {
        title: 'Gere les bookings de tes artistes',
        desc: 'Une fois un artiste dans ton roster, tu vois ses bookings et peux echanger avec les promoteurs en son nom.',
      },
      {
        title: 'Centralise la communication',
        desc: 'Accede a la messagerie de chaque booking, partage les documents et coordonne les details logistiques.',
      },
    ],
  },
];

const FAQ_SECTIONS = [
  {
    label: 'Comptes et inscription',
    items: [
      {
        q: 'Puis-je avoir plusieurs roles avec un seul compte ?',
        a: 'Non, chaque compte est lie a un seul role (artiste, promoteur ou booker). Si tu as besoin de plusieurs roles, cree un compte par role avec des emails differents.',
      },
      {
        q: 'Je suis booker, puis-je creer les comptes de mes artistes ?',
        a: 'Non. Chaque artiste doit creer son propre compte avec son email personnel, completer son profil et ses disponibilites. Ensuite tu l\'invites dans ton roster depuis ton dashboard booker. C\'est une question d\'autonomie : chaque artiste garde le controle de son compte et de ses donnees.',
      },
      {
        q: 'Comment verifier mon email ?',
        a: 'Un email de verification t\'est envoye automatiquement a l\'inscription. Clique sur le lien dans l\'email. Le lien est valide 24h. Si tu ne le recois pas, verifie tes spams.',
      },
    ],
  },
  {
    label: 'Bookings et equilibre de la plateforme',
    items: [
      {
        q: 'Pourquoi un artiste ne peut-il pas se proposer pour un evenement ?',
        a: 'Striderr fonctionne sur un modele d\'invitation : c\'est le promoteur qui contacte l\'artiste. Ce choix garantit que chaque proposition correspond a un evenement reel et a un besoin precis. Ca protege les artistes du demarchage non sollicite, et ca permet aux promoteurs de garder la main sur leur programmation. L\'artiste rend son profil attractif et gere ses dispos : c\'est ca qui attire les propositions.',
      },
      {
        q: 'Qu\'est-ce que la feuille de route ?',
        a: 'C\'est un document PDF genere automatiquement quand un booking est accepte. Il regroupe toutes les infos pratiques : lieu, horaires, contacts, notes techniques, catering, parking. Tu peux aussi la regenerer manuellement avec des infos supplementaires.',
      },
      {
        q: 'Un promoteur peut-il annuler un booking ?',
        a: 'Oui, le promoteur peut annuler un booking a tout moment, quel que soit son statut. L\'artiste (et son booker s\'il en a un) seront notifies par email et en temps reel.',
      },
    ],
  },
  {
    label: 'Booker / Agent',
    items: [
      {
        q: 'Qui peut inviter qui ? (booker / artiste)',
        a: 'C\'est bidirectionnel. Le booker peut inviter un artiste dans son roster, et l\'artiste peut aussi envoyer une demande a un booker. Dans les deux cas, l\'autre partie doit accepter.',
      },
      {
        q: 'Un artiste peut-il avoir plusieurs bookers ?',
        a: 'Non, un artiste ne peut etre manage que par un seul booker a la fois. Il doit d\'abord se separer de son booker actuel avant d\'en rejoindre un autre.',
      },
      {
        q: 'Que peut faire un booker sur les bookings de ses artistes ?',
        a: 'Le booker voit tous les bookings de ses artistes manages, peut echanger avec les promoteurs en leur nom, et acceder a la messagerie et aux fichiers de chaque booking.',
      },
    ],
  },
];

export default function HowItWorks() {
  const { isAuthenticated } = useAuth();
  const signupTo = isAuthenticated ? '/dashboard' : '/register';

  const [activeTab, setActiveTab] = useState('guides');
  const [openFaq, setOpenFaq] = useState(null);
  const [expandedRole, setExpandedRole] = useState(null);

  function toggleFaq(key) {
    setOpenFaq(openFaq === key ? null : key);
  }

  function toggleRole(id) {
    setExpandedRole(expandedRole === id ? null : id);
  }

  return (
    <div className="animate-fadeIn">
      {/* ── Hero ── */}
      <section className="relative th-bg border-b th-border overflow-hidden">
        <span className="hidden sm:block absolute top-7 right-6 lg:right-8 th-kicker">
          Guide · FAQ
        </span>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="th-kicker th-kicker-accent mb-5 animate-fadeInUp">
            Mode d'emploi
          </div>
          <h1
            className="th-display-xl max-w-[18ch] animate-fadeInUp"
            style={{ animationDelay: '0.05s' }}
          >
            Comment <em>ca marche</em> ?
          </h1>
          <p
            className="th-mono-meta mt-7 max-w-[60ch] leading-loose animate-fadeInUp"
            style={{ animationDelay: '0.1s' }}
          >
            Trois roles, un objectif : connecter artistes, promoteurs et bookers
            sans friction. Voici comment chacun utilise Striderr.
          </p>
        </div>
      </section>

      {/* ── Tabs ── */}
      <div className="border-b th-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-0">
          <button
            onClick={() => setActiveTab('guides')}
            className={`py-3.5 px-5 font-mono text-[11px] uppercase tracking-[0.15em] border-b-2 transition-colors duration-150 ${
              activeTab === 'guides'
                ? 'border-accent text-accent'
                : 'border-transparent th-text-secondary hover:th-text'
            }`}
          >
            Guides par role
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`py-3.5 px-5 font-mono text-[11px] uppercase tracking-[0.15em] border-b-2 transition-colors duration-150 ${
              activeTab === 'faq'
                ? 'border-accent text-accent'
                : 'border-transparent th-text-secondary hover:th-text'
            }`}
          >
            Questions frequentes
          </button>
        </div>
      </div>

      {/* ── Guides par role ── */}
      {activeTab === 'guides' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          {ROLES.map((role, ri) => (
            <div
              key={role.id}
              className={`border th-border mb-6 animate-fadeInUp`}
              style={{ animationDelay: `${0.06 * ri}s` }}
            >
              {/* Role header */}
              <button
                onClick={() => toggleRole(role.id)}
                className="w-full flex items-baseline gap-5 p-6 sm:p-8 text-left transition-colors duration-150 hover:th-bg-surface"
              >
                <span className="th-kicker th-kicker-accent flex-shrink-0">{role.n}</span>
                <div className="flex-1 min-w-0">
                  <h2 className="th-display-md">{role.label}</h2>
                  <p className="th-mono-meta mt-1.5 normal-case">{role.sub}</p>
                </div>
                <span className="th-text-muted font-mono text-xs flex-shrink-0">
                  {expandedRole === role.id ? '−' : '+'}
                </span>
              </button>

              {/* Role steps */}
              {expandedRole === role.id && (
                <div className="border-t th-border animate-fadeIn">
                  {role.important && (
                    <div className="mx-6 sm:mx-8 mt-6 border th-border bg-accent/10 p-4">
                      <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-accent leading-relaxed">
                        Important : {role.important}
                      </p>
                    </div>
                  )}

                  {role.steps.map((step, si) => (
                    <div
                      key={si}
                      className={`flex gap-5 px-6 sm:px-8 py-5 ${
                        si < role.steps.length - 1 ? 'border-b th-border-muted' : ''
                      }`}
                    >
                      <span className="font-mono text-xs text-accent min-w-[28px] pt-0.5">
                        {String(si + 1).padStart(2, '0')}.
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-[13px] font-bold uppercase tracking-[0.1em] th-text">
                          {step.title}
                        </p>
                        <p className="th-mono-meta mt-1.5 normal-case leading-relaxed">
                          {step.desc}
                        </p>
                        {step.note && (
                          <span className="inline-block mt-2.5 th-badge th-border text-accent border-accent">
                            {step.note}
                          </span>
                        )}
                        {step.explain && (
                          <p className="th-serif-body mt-3 text-sm border-l-2 border-accent/30 pl-4 italic opacity-80">
                            {step.explain}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* CTA */}
          <div
            className="border-t th-border pt-10 mt-10 text-center animate-fadeInUp"
            style={{ animationDelay: '0.2s' }}
          >
            <div className="th-kicker mb-3">Pret a commencer ?</div>
            <Link to={signupTo} className="th-btn-accent">
              Creer mon compte
            </Link>
          </div>
        </section>
      )}

      {/* ── FAQ ── */}
      {activeTab === 'faq' && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          {FAQ_SECTIONS.map((section, si) => (
            <div
              key={si}
              className={`${si > 0 ? 'mt-12' : ''} animate-fadeInUp`}
              style={{ animationDelay: `${0.06 * si}s` }}
            >
              <div className="th-kicker th-kicker-accent mb-5">{section.label}</div>

              <div className="border-t th-border">
                {section.items.map((item, ii) => {
                  const key = `${si}-${ii}`;
                  const isOpen = openFaq === key;
                  return (
                    <div key={key} className="border-b th-border">
                      <button
                        onClick={() => toggleFaq(key)}
                        className="w-full flex items-baseline gap-4 py-5 px-1 text-left group transition-colors duration-150"
                      >
                        <span className="font-mono text-xs text-accent flex-shrink-0 pt-0.5">
                          {isOpen ? '−' : '+'}
                        </span>
                        <span className="th-display-md text-lg sm:text-xl group-hover:text-accent transition-colors duration-150">
                          {item.q}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="pl-7 pb-5 pr-4 animate-fadeIn">
                          <p className="th-serif-body text-base leading-relaxed max-w-[70ch]">
                            {item.a}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* CTA */}
          <div
            className="border-t th-border pt-10 mt-12 text-center animate-fadeInUp"
            style={{ animationDelay: '0.2s' }}
          >
            <div className="th-kicker mb-3">D'autres questions ?</div>
            <p className="th-mono-meta normal-case leading-relaxed max-w-[50ch] mx-auto mb-5">
              Contacte-nous ou cree ton compte pour decouvrir la plateforme par toi-meme.
            </p>
            <Link to={signupTo} className="th-btn-accent">
              Creer mon compte
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
